import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Skeleton from "@mui/material/Skeleton";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ReactComponent as CloseIcon } from "../../../../../assets/images/x.svg";
import SearchInput from "../../../../common/SearchInputField";
import NoSearch from "../../../../common/NoSearch";
import DropdownSelectionTick from "../../DropdownSelectionTick";
import { ListCoRoleName } from "../../../../../store/features/ListCoUserByRole";
import { mapAgentListItem } from "../../../../../utils/teamMappers";
import { colorCodesForAvatar } from "../../../../../constants/index";
import { chracterPosition } from "../../../../../utils/index";

const rowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1,
  py: 1,
  borderRadius: "8px",
  cursor: "pointer",
  border: "none",
  outline: "none",
  "&:hover": { backgroundColor: "#F9FAFA" },
};

export default function TeamAddMembersModal({
  open,
  onClose,
  onApply,
  existingMemberIds = [],
  agentOptions: agentOptionsProp = [],
  agentsLoading: agentsLoadingProp = false,
}) {
  const dispatch = useDispatch();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedIds(new Set());
      return;
    }

    if (agentOptionsProp.length > 0) {
      setAgents(agentOptionsProp);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchAgents = async () => {
      setLoading(true);
      try {
        const response = await dispatch(
          ListCoRoleName({ name: "agent,admin,admin_with_billing" })
        );
        if (cancelled) return;
        if (response?.payload?.status === 200) {
          setAgents((response.payload.data || []).map(mapAgentListItem).filter((a) => a.id));
        } else {
          toast.error("Failed to load agents");
          setAgents([]);
        }
      } catch {
        if (!cancelled) toast.error("Failed to load agents");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAgents();
    return () => {
      cancelled = true;
    };
  }, [open, dispatch, agentOptionsProp]);

  const availableAgents = useMemo(
    () => agents.filter((a) => !existingMemberIds.includes(a.id)),
    [agents, existingMemberIds]
  );

  const filteredAgents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableAgents;
    return availableAgents.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q)
    );
  }, [availableAgents, searchQuery]);

  const isLoading = loading || (agentsLoadingProp && !agents.length);

  const toggleAgent = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    if (!selectedIds.size) return;
    const picked = availableAgents.filter((a) => selectedIds.has(a.id));
    onApply(picked);
    onClose();
  };

  const handleClose = () => {
    setSearchQuery("");
    setSelectedIds(new Set());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          height: 432,
          maxWidth: "95vw",
          maxHeight: "95vh",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          p: "24px",
          position: "relative",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "18px",
            mb: 2,
            flexShrink: 0,
          }}
        >
          Add Team
        </Typography>

        <Box sx={{ flexShrink: 0 }}>
        <SearchInput
          placeholder="Search team"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="SearchInputField-team-members"
          iconPosition="left"
          iconGap={8}
          style={{ width: "100%" }}
          inputStyle={{ width: "100%" }}
          noTitle
        />
        </Box>

        <Box
          sx={{
            mt: 2,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="55%" height={18} sx={{ borderRadius: "4px" }} />
                    <Skeleton variant="text" width="35%" height={14} sx={{ borderRadius: "4px" }} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : filteredAgents.length === 0 ? (
            <NoSearch
              title="No agents found."
              description="Clear your search and try again."
            />
          ) : (
            filteredAgents.map((agent) => {
              const isSelected = selectedIds.has(agent.id);
              const letter = (agent.name || "?").substring(0, 1).toUpperCase();
              return (
                <Box
                  key={agent.id}
                  onClick={() => toggleAgent(agent.id)}
                  sx={{
                    ...rowSx,
                    backgroundColor: isSelected ? "#F9FAFA" : "transparent",
                    "&:hover": { backgroundColor: isSelected ? "#F9FAFA" : "#F9FAFA" },
                  }}
                >
                  <Avatar
                    src={agent.avatar || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 14,
                      flexShrink: 0,
                      bgcolor:
                        colorCodesForAvatar[chracterPosition(letter)] || "#405261",
                    }}
                  >
                    {letter}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: "#061522",
                        fontFamily: "Inter",
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: "18px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {agent.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "18px",
                      }}
                    >
                      {agent.role || "Agent"}
                    </Typography>
                  </Box>
                  {isSelected && (
                    <Box sx={{ flexShrink: 0, pr: 0.5, display: "flex", alignItems: "center" }}>
                      <DropdownSelectionTick />
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 2, flexShrink: 0 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              minWidth: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selectedIds.size}
            sx={{
              width: 176,
              minWidth: 176,
              height: 40,
              borderRadius: "8px",
              background: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#0077CC" },
              "&.Mui-disabled": {
                color: "#93A1B0",
                bgcolor: "#F9FAFA",
                border: "1px solid #C5CED6",
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
